import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { FormContainer, Treturn, useFetch } from "@4uhub/lib4uhub";
import {
  createIService,
  getIServiceById,
  updateIServiceById,
} from "../../../../../services/iService.service";
import { iServiceSchema, TIServiceForm } from "./iServiceSchema";
import { IServiceFields } from "./IServiceFields";
import { useIServiceType } from "./IServiceType/useIServiceType";
import { IBusInternalTokenExternal } from "./IServiceType/models";

const translationPath = "page.4uBus.register.iService.";

const IService = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(createIService);

  const { sendRequest: update, loading: uLoading } =
    useFetch(updateIServiceById);

  const { sendRequest: get, loading: gLoading } = useFetch(getIServiceById);

  const { jsonParser, busInternalTokenGet, iServiceTypeNameGet } =
    useIServiceType();

  const saveHandler = useCallback(
    (v: TIServiceForm) => {
      const certId =
        v.si_pni?.companyDigitalCertificate?.id ??
        v.certillion?.companyDigitalCertificate?.id;

      return save({
        ...v,
        companyDigitalCertificateId: certId,
        uBusUserId: v.uBusUser?.id,
        iServiceTypeId: v.iServiceType!.id,
        methodTypeId: v.methodType.id,
      });
    },
    [save],
  );

  const updateHandler = useCallback(
    (v: TIServiceForm, id: string) => {
      const certId =
        v.si_pni?.companyDigitalCertificate?.id ??
        v.certillion?.companyDigitalCertificate?.id;

      return update({
        item: {
          ...v,
          companyDigitalCertificateId: certId,
          uBusUserId: v.uBusUser?.id,
          iServiceTypeId: v.iServiceType!.id,
          methodTypeId: v.methodType.id,
        },
        id: id,
      });
    },
    [update],
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TIServiceForm>> => {
      const { data, success } = await get(id);
      if (data && success) {
        const iServiceTypeName = iServiceTypeNameGet(data.iServiceType.code);
        const configs =
          iServiceTypeName &&
          jsonParser<TIServiceForm[typeof iServiceTypeName]>(data.configs);
        const uBusUserValues = data.uBusUser && {
          busInternalToken: data.uBusUser,
        };
        const busInternalToken = busInternalTokenGet(
          data.uBusUserExternal
            ? (configs as IBusInternalTokenExternal)
            : uBusUserValues,
          data.uBusUserExternal,
        );

        const newData: TIServiceForm = {
          ...data,
          ...(busInternalToken
            ? { busInternalToken: busInternalToken }
            : iServiceTypeName && {
                [iServiceTypeName]: configs,
              }),
        };
        return { data: newData, success: true };
      }

      return { data, success };
    },
    [get, busInternalTokenGet, iServiceTypeNameGet, jsonParser],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TIServiceForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "i_service")}
      schema={iServiceSchema}
      subtitle={t(translationPath + "new_i_service")}
      subtitleWatchField="name"
      fields={() => <IServiceFields />}
    />
  );
};

export default IService;
